// src/components/Ticker.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './Ticker.css';

/**
 * Ticker
 * props:
 *  - items: any[]
 *  - renderItem?: (item) => ReactNode  (should output an element with class "ticker-chip" + optional "up|down|steady")
 *  - speedPxPerSec?: number (default 80)
 *  - storageKey?: string
 *
 * Update:
 *  - Persists progress so when you navigate away/back, the ticker resumes where it left off.
 *  - DOES NOT change markup/classes (so your look stays the same).
 *
 * Notes on why this works reliably in an SPA:
 *  - Default storage key is route-scoped so global vs regional pages don't overwrite each other.
 *  - We restore by setting Web Animations API currentTime (negative delay fallback).
 */
export default function Ticker({
  items = [],
  renderItem,
  speedPxPerSec = 80,
  storageKey,
}) {
  const wrapRef = useRef(null);
  const railRef = useRef(null);
  const [duration, setDuration] = useState(30);

  // Persist only the *progress ratio* (0..1) so it survives duration recalcs.
  const progressRatioRef = useRef(0);

  // Build a stable "page identity" so progress doesn't get overwritten by other routes.
  // If caller supplies storageKey, we use it. Otherwise we scope by current path.
  const resolvedStorageKey = useMemo(() => {
    if (storageKey && String(storageKey).trim()) return String(storageKey);
    const path =
      (typeof window !== 'undefined' && window.location && window.location.pathname) || 'unknown';
    return `popdaq:ticker:progress:${path}`;
  }, [storageKey]);

  // Keep latest key in a ref for event handlers.
  const storageKeyRef = useRef(resolvedStorageKey);
  useEffect(() => {
    storageKeyRef.current = resolvedStorageKey;
  }, [resolvedStorageKey]);

  // Load saved progress ratio when key changes (e.g., route change)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(resolvedStorageKey);
      const n = raw ? Number(raw) : 0;
      if (Number.isFinite(n) && n >= 0 && n < 1) progressRatioRef.current = n;
      else progressRatioRef.current = 0;
    } catch {
      progressRatioRef.current = 0;
    }
  }, [resolvedStorageKey]);

  const recalc = () => {
    const rail = railRef.current;
    if (!rail) return;

    // half width because we render items twice
    const half = Math.max(rail.scrollWidth / 2, 1);
    const speed = Math.max(Number(speedPxPerSec) || 80, 1);
    const secs = Math.max(half / speed, 6); // clamp so it's never too fast
    setDuration(secs);
  };

  useLayoutEffect(recalc);

  useEffect(() => {
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [items, speedPxPerSec]);

  const readAndPersistProgress = () => {
    const rail = railRef.current;
    if (!rail) return;

    const key = storageKeyRef.current;

    try {
      const anims = rail.getAnimations ? rail.getAnimations() : [];
      const anim = anims && anims.length ? anims[0] : null;

      const durMs = duration * 1000;

      if (anim && Number.isFinite(anim.currentTime) && Number.isFinite(durMs) && durMs > 0) {
        const ms = Number(anim.currentTime);
        // Normalize into [0,1)
        const ratio = (((ms % durMs) + durMs) % durMs) / durMs;
        progressRatioRef.current = ratio;
        sessionStorage.setItem(key, String(ratio));
        return;
      }

      // Fallback: keep last known ratio
      sessionStorage.setItem(key, String(progressRatioRef.current || 0));
    } catch {
      // ignore
    }
  };

  // Persist on tab hide + page lifecycle
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') readAndPersistProgress();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', readAndPersistProgress);
    window.addEventListener('beforeunload', readAndPersistProgress);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', readAndPersistProgress);
      window.removeEventListener('beforeunload', readAndPersistProgress);
      readAndPersistProgress();
    };
  }, [duration]);

  // Also persist when the "page identity" changes (route key changes) or items change significantly.
  // This catches SPA navigations where unmount doesn't happen the way you expect.
  useEffect(() => {
    return () => {
      readAndPersistProgress();
    };

  }, [resolvedStorageKey, items]);

  // Restore saved progress after duration is known.
  // Prefer Web Animations API currentTime (reliable jump); fallback to negative animation-delay.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;

    const ratio = progressRatioRef.current || 0;
    const durMs = duration * 1000;

    let raf1 = 0;
    let raf2 = 0;

    const apply = () => {
      const anims = rail.getAnimations ? rail.getAnimations() : [];
      const anim = anims && anims.length ? anims[0] : null;

      if (anim && Number.isFinite(durMs) && durMs > 0) {
        try {
          // Jump to the right point in the loop
          anim.currentTime = ratio * durMs;
          // Ensure it plays
          if (anim.playState !== 'running') anim.play();
          return;
        } catch {
          // fall through to delay fallback
        }
      }

      // Fallback: negative delay. To force the browser to honor it, briefly restart animation.
      try {
        rail.style.animationDelay = ratio > 0 ? `-${ratio * duration}s` : '0s';
        rail.style.animation = 'none';
        // force reflow
        // eslint-disable-next-line no-unused-expressions
        rail.offsetHeight;
        rail.style.animation = '';
      } catch {
        // ignore
      }
    };

    // Wait a couple frames so the CSS animation is definitely instantiated
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(apply);
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [duration, resolvedStorageKey]);

  const Chip = ({ item, index }) => {
    if (renderItem) return <React.Fragment key={index}>{renderItem(item)}</React.Fragment>;
    return (
      <button key={index} type="button" className="ticker-chip steady">
        {String(item)}
      </button>
    );
  };

  const doubled = [...items, ...items];

  return (
    <div ref={wrapRef} className="ticker" aria-label="trending" role="region" tabIndex={-1}>
      <div ref={railRef} className="ticker__rail" style={{ ['--ticker-d']: `${duration}s` }}>
        {doubled.map((it, i) => (
          <Chip item={it} index={i} key={i} />
        ))}
      </div>
    </div>
  );
}
